// V5.34 : Page "Gérer mon dossier" - Affichage pour missions VALIDATED
// Route: /missions/manage?mission=xxx
// V5.34 FIX: Utilise la MÊME sidebar que le Dashboard (ContextualSidebar)

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { 
  Home, Settings, CreditCard, Gift, Shield, User, LogOut, ArrowLeft,
  CheckCircle, Loader2, AlertCircle, FileText, Building2, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ValidatedMissionView from '../components/missions/ValidatedMissionView';
import { ContextualSidebar } from '../components/layout/ContextualSidebar';

// V5.34 : Configuration du menu - PLUS UTILISÉ, on utilise ContextualSidebar
// Gardé pour référence historique
const STANDARD_NAV_ITEMS = [
  { id: 'overview', label: 'Accueil', icon: Home },
  { id: 'missions', label: 'Mes Missions', icon: Briefcase },
  { id: 'payment', label: 'Paiement', icon: CreditCard },
  { id: 'referral', label: 'Parrainage', icon: Gift },
  { id: 'data-security', label: 'Zero Trace', icon: Shield }
];

const MissionManagePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const missionId = searchParams.get('mission');
  
  const [user, setUser] = useState(null);
  const [mission, setMission] = useState(null);
  const [missionData, setMissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('missions'); // V5.34 FIX: Déplacer le useState ici
  
  // Charger les données de la mission
  useEffect(() => {
    const loadMission = async () => {
      if (!missionId) {
        setError('ID de mission manquant');
        setLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Charger l'utilisateur
        const userRes = await axios.get(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        setUser(userRes.data);
        
        // Charger la mission
        const missionRes = await axios.get(`${API}/missions/${missionId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        
        // V5.33 FIX: La route renvoie { mission: {...} }
        const missionWrapper = missionRes.data;
        const missionObj = missionWrapper.mission || missionWrapper;
        
        // V5.33 FIX: Extraire le mandat_id depuis le mandat
        const mandatId = missionObj.mandat?.mandat_id || missionObj.details?.mandat_id;
        
        // V5.67 FIX: La route /missions/{id} renvoie { mission: { details, subscription, mandat, ... } }
        // Les données V5.67 sont dans missionObj.details (pas dans missionWrapper top-level)
        const details67 = missionObj.details || {};
        
        // Construire l'objet consigne_expert depuis details.consigne_expert (string → objet)
        const consigneRaw = details67.consigne_expert || '';
        const consigneExpertObj = consigneRaw
          ? { text: consigneRaw, updated_at: details67.consigne_expert_updated_at }
          : (missionWrapper.consigne_expert || {});
        
        // Construire l'objet mission avec le mandat_id + données complètes V5.67
        const fullMission = {
          ...missionObj,
          id: missionId,
          _id: missionId,
          mandat_id: mandatId,
          // V5.67 FIX: Lire depuis missionObj.details (chemin correct)
          consigne_expert: consigneExpertObj,
          user_email: details67.mission_data?.client?.email
            || details67.mission_data?.email
            || missionWrapper.client?.email
            || userRes.data?.email
            || '',
          display_id: missionWrapper.display_id || details67.mission_id || missionId,
          admin_status: details67.status || missionObj.status || 'nouveau'
        };
        setMission(fullMission);
        
        // Extraire les données de mission depuis les détails
        const details = missionObj.details || {};
        const formData = details.form_data || {};
        
        const extractedMissionData = details.mission_data || {
          id: missionId,
          mandat_id: mandatId, // V5.33 FIX: Inclure le mandat_id
          step1_adresses: {
            depart: {
              adresse: formData.ancienne_adresse || '',
              code_postal: formData.ancienne_code_postal || '',
              ville: formData.ancienne_ville || ''
            },
            arrivee: {
              adresse: formData.nouvelle_adresse || '',
              code_postal: formData.nouvelle_code_postal || '',
              ville: formData.nouvelle_ville || ''
            },
            date_demenagement: formData.date_demenagement || ''
          },
          categories: formData.strategy_choices || {},
          signed_at: details.validated_at || details.updated_at
        };
        
        setMissionData(extractedMissionData);
        
      } catch (err) {
        console.error('Erreur chargement mission:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.detail || 'Erreur lors du chargement');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadMission();
  }, [missionId, navigate]);
  
  // Handler de déconnexion
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Déconnexion réussie');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement de votre dossier...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white font-bold mb-2">Erreur</p>
          <p className="text-gray-400 mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-cyan-500 text-black">
            Retour au Dashboard
          </Button>
        </div>
      </div>
    );
  }
  
  // V5.34 FIX : activeTab déplacé en haut du composant
  
  return (
    <div className="min-h-screen bg-void flex">
      {/* V5.34 FIX : UTILISER LA MÊME SIDEBAR QUE LE DASHBOARD */}
      <ContextualSidebar
        context="dashboard"
        activeTab={activeTab}
        setActiveTab={(tab) => {
          // Naviguer vers le dashboard avec le bon tab
          navigate(`/dashboard?tab=${tab}`);
        }}
        user={user}
        onLogout={handleLogout}
      />
      
      {/* Contenu principal */}
      <main className="flex-1 md:ml-64 p-8">
        {/* Bouton retour */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard?tab=missions')}
            className="border-gray-700 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à mes missions
          </Button>
        </div>
        
        {/* V5.33 : Vue du dossier validé */}
        <ValidatedMissionView
          mission={mission}
          missionData={missionData}
          user={user}
          onUploadDocument={() => {
            // Recharger les données après upload
            toast.success('Document uploadé');
          }}
        />
      </main>
    </div>
  );
};

export default MissionManagePage;
